import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2Icon, ShieldAlertIcon, Trash2Icon } from 'lucide-react'
import { toast } from 'sonner'
import { RoleMultiSelect } from '@/components/auth/admin/admin-users'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { api } from '@/lib/api'
import { roles } from '@/lib/roles'
import { parseAmpProjectSubject } from '../../../shared/amp-subject'
import { permissionsFor, roleNames } from '../../../shared/permissions'

export const Route = createFileRoute('/_authed/amp-projects')({
	// ?subject= comes from the approval link the CLI prints when an orb's project isn't trusted yet.
	validateSearch: (search: Record<string, unknown>): { subject?: string } => ({
		subject: typeof search.subject === 'string' ? search.subject : undefined
	}),
	component: AmpProjects
})

type AmpProject = { subject: string; label: string; role: string; approvedBy: string; createdAt: string }

const queryKey = ['amp-projects']

const useProjects = () => useQuery({ queryKey, queryFn: () => api<AmpProject[]>('/api/amp-projects') })

const formatDate = (value: string) => new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(value))

function AmpProjects() {
	const { user } = Route.useRouteContext()
	const { subject } = Route.useSearch()

	if (!permissionsFor(user.role).user?.includes('set-role'))
		return (
			<div className="flex min-h-64 flex-col items-center justify-center gap-3 rounded-xl border border-dashed bg-card p-8 text-center">
				<ShieldAlertIcon className="size-8 text-primary" />
				<h2 className="font-semibold">Admins only</h2>
				<p className="max-w-md text-sm text-muted-foreground">Only admins can decide which Amp projects may use the edclub CLI.</p>
			</div>
		)

	return (
		<section className="flex flex-col gap-6">
			<div className="flex flex-col gap-1">
				<h1 className="text-xl font-semibold tracking-tight">Amp projects</h1>
				<p className="text-sm text-muted-foreground">
					Orbs in these Amp projects can sign in to the edclub CLI with their Amp identity, with the roles granted here.
				</p>
			</div>
			{subject !== undefined && <Approval subject={subject} />}
			<ProjectList highlight={subject} />
		</section>
	)
}

function Approval({ subject }: { subject: string }) {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const projects = useProjects()
	const parsed = parseAmpProjectSubject(subject)
	const trusted = projects.data?.find((project) => project.subject === subject)
	const [label, setLabel] = useState('')
	const [selectedRoles, setSelectedRoles] = useState<string[]>(['registry:edit'])
	const approve = useMutation({
		mutationFn: () => api<AmpProject>('/api/amp-projects', { method: 'POST', body: JSON.stringify({ subject, label, role: selectedRoles.join(',') }) }),
		onSuccess: (project) => {
			toast.success(`${project.label} can now use the edclub CLI.`)
			return queryClient.invalidateQueries({ queryKey })
		}
	})
	const close = () => navigate({ to: '/amp-projects', search: {} })

	if (!parsed)
		return (
			<Card className="border-destructive/40">
				<CardHeader>
					<CardTitle className="flex items-center gap-2"><ShieldAlertIcon className="size-5 text-destructive" />Not a project-scoped Amp subject</CardTitle>
					<CardDescription>
						Only <code>workspace:&lt;id&gt;:project:&lt;id&gt;</code> or <code>user:&lt;id&gt;:project:&lt;id&gt;</code> can be trusted. The orb must mint its ID token with{' '}
						<code>--subject-scope project</code>.
					</CardDescription>
				</CardHeader>
				<CardContent><p className="font-mono text-xs break-all text-muted-foreground">{subject || '(empty)'}</p></CardContent>
				<CardFooter><Button variant="outline" onClick={close}>Dismiss</Button></CardFooter>
			</Card>
		)

	if (projects.isPending) return <Skeleton className="h-64 w-full rounded-xl" />

	if (trusted)
		return (
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<CheckCircle2Icon className="size-5 text-primary" />
						{approve.isSuccess ? 'Approved' : 'Already trusted'}
					</CardTitle>
					<CardDescription>
						<strong className="text-foreground">{trusted.label}</strong> is trusted with {roles(trusted).join(', ')} (approved by {trusted.approvedBy} on{' '}
						{formatDate(trusted.createdAt)}). Orbs in this project can run their edclub CLI login again.
					</CardDescription>
				</CardHeader>
				<CardContent><SubjectParts subject={subject} /></CardContent>
				<CardFooter><Button variant="outline" onClick={close}>Done</Button></CardFooter>
			</Card>
		)

	return (
		<Card>
			<form
				className="flex flex-col gap-6"
				onSubmit={(event) => {
					event.preventDefault()
					approve.mutate()
				}}
			>
				<CardHeader>
					<CardTitle>Approve Amp project</CardTitle>
					<CardDescription>
						An orb in this Amp project asked to use the edclub CLI. Every orb in the project will act with the roles you grant, so approve only projects you recognise.
					</CardDescription>
				</CardHeader>
				<CardContent className="flex flex-col gap-6">
					<SubjectParts subject={subject} />
					<FieldGroup className="max-w-xl">
						<Field>
							<FieldLabel htmlFor="amp-project-label">Label</FieldLabel>
							<Input id="amp-project-label" value={label} maxLength={100} placeholder="e.g. Experience registry deploys" onChange={(event) => setLabel(event.target.value)} />
						</Field>
						<Field>
							<FieldLabel htmlFor="amp-project-roles">Roles</FieldLabel>
							<RoleMultiSelect id="amp-project-roles" label="Roles" roles={roleNames} value={selectedRoles} onChange={setSelectedRoles} />
							<p className="text-xs text-muted-foreground"><code>registry:edit</code> can only deploy experiences to the registry.</p>
						</Field>
						{approve.isError && <FieldError>{approve.error.message}</FieldError>}
					</FieldGroup>
				</CardContent>
				<CardFooter className="gap-3">
					<Button type="submit" disabled={!label.trim() || approve.isPending}>{approve.isPending ? 'Approving…' : 'Approve'}</Button>
					<Button type="button" variant="outline" onClick={close}>Cancel</Button>
				</CardFooter>
			</form>
		</Card>
	)
}

function SubjectParts({ subject }: { subject: string }) {
	const { ownerType, ownerId, projectId } = parseAmpProjectSubject(subject)!
	return (
		<dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[auto_1fr]">
			<dt className="text-muted-foreground">{ownerType === 'workspace' ? 'Workspace' : 'Owner (user)'}</dt>
			<dd className="font-mono break-all">{ownerId}</dd>
			<dt className="text-muted-foreground">Project</dt>
			<dd className="font-mono break-all">{projectId}</dd>
			<dt className="text-muted-foreground">Subject</dt>
			<dd className="font-mono text-xs break-all text-muted-foreground">{subject}</dd>
		</dl>
	)
}

function ProjectList({ highlight }: { highlight?: string }) {
	const queryClient = useQueryClient()
	const projects = useProjects()
	const [revoking, setRevoking] = useState<AmpProject | null>(null)
	const revoke = useMutation({
		mutationFn: (project: AmpProject) => api<void>(`/api/amp-projects/${encodeURIComponent(project.subject)}`, { method: 'DELETE' }),
		onSuccess: (_, project) => {
			toast.success(`${project.label} can no longer use the edclub CLI.`)
			setRevoking(null)
			return queryClient.invalidateQueries({ queryKey })
		}
	})

	return (
		<>
			<div className="overflow-hidden rounded-lg border">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead className="w-1/2">Project</TableHead>
							<TableHead className="hidden md:table-cell">Roles</TableHead>
							<TableHead className="hidden lg:table-cell">Approved</TableHead>
							<TableHead className="w-0"><span className="sr-only">Actions</span></TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{projects.isPending ? (
							<TableRow><TableCell colSpan={4}><Skeleton className="h-10 w-full" /></TableCell></TableRow>
						) : projects.isError ? (
							<TableRow><TableCell colSpan={4} className="h-40 text-center text-destructive">{projects.error.message}</TableCell></TableRow>
						) : projects.data.length ? (
							projects.data.map((project) => (
								<TableRow key={project.subject} aria-selected={project.subject === highlight}>
									<TableCell className="whitespace-normal">
										<div className="font-medium">{project.label}</div>
										<div className="font-mono text-xs break-all text-muted-foreground">{project.subject}</div>
									</TableCell>
									<TableCell className="hidden md:table-cell">
										<div className="flex flex-wrap gap-1">{roles(project).map((role) => <Badge key={role} variant="outline">{role}</Badge>)}</div>
									</TableCell>
									<TableCell className="hidden lg:table-cell">
										<div>{formatDate(project.createdAt)}</div>
										<div className="text-xs text-muted-foreground">{project.approvedBy}</div>
									</TableCell>
									<TableCell>
										<Button variant="outline" size="sm" onClick={() => setRevoking(project)}><Trash2Icon />Revoke</Button>
									</TableCell>
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell colSpan={4} className="h-40 text-center whitespace-normal">
									<strong className="block font-medium">No trusted Amp projects</strong>
									<span className="text-muted-foreground">When an orb's CLI login is refused, the CLI prints a link that opens the approval form here.</span>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
			<AlertDialog open={Boolean(revoking)} onOpenChange={(open) => !open && (setRevoking(null), revoke.reset())}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Revoke {revoking?.label}?</AlertDialogTitle>
						<AlertDialogDescription>
							Orbs in this project lose CLI access: their refresh tokens stop working now, and access tokens already issued expire within 15 minutes.
						</AlertDialogDescription>
					</AlertDialogHeader>
					{revoke.isError && <FieldError>{revoke.error.message}</FieldError>}
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							disabled={revoke.isPending}
							onClick={(event) => {
								event.preventDefault()
								if (revoking) revoke.mutate(revoking)
							}}
						>
							Revoke
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
